import { useContext } from 'react';
import * as R from 'ramda';

import textConstants from '@atom/constants/textConstants';
import activityIcon from '@atom/images/activityIcon.svg';
import { Icon, Snackbar, Switch } from '@atom/mui';
import {
  CategoryIcon,
  ChevronRightIcon,
  CodeIcon,
  WorkIcon,
} from '@atom/mui/icons';
import {
  ColumnsData,
  DashboardCard,
  DashboardCardContentState,
  DashboardCardType,
} from '@atom/types/dashboard';
import { updateAtId } from '@atom/utilities/stateUtilities';

import DashboardContext from '../DashboardContext';
import { getColumnIndex, getColumnsData } from '../dashboardUtilities';

import { ModalView } from './CustomizeDashboardModal';
import TeamIcon from './TeamIcon';

import classes from './customizeDashboardModal.module.css';

interface Props {
  setView: (view: ModalView) => void;
}

const styles = {
  switchLabel: {
    marginLeft: 0,
    width: '100%',
    justifyContent: 'space-between',
  },
};

const getOption = (card, onChange, disabled) => (
  <div key={card.id} className={classes.customizeDashboardOption}>
    <Switch
      labelPlacement="start"
      label={
        <div className={classes.customizeDashboardLabel}>
          <div className={classes.optionIcon}>{card.icon}</div>
          <div>
            <div className={classes.customizeOptionHeading}>{card.heading}</div>
            <span className={classes.customizeOptionSubheading}>
              {card.subheading}
            </span>
          </div>
        </div>
      }
      sx={styles.switchLabel}
      checked={card.order !== -1}
      onChange={() => onChange(card)}
      disabled={disabled}
    />
  </div>
);

const DEFAULT_CARD_TYPE_OPTIONS = {
  [DashboardCardType.OPEN_WORK]: {
    icon: <Icon icon={WorkIcon} />,
    heading: 'My Open Work',
    subheading: 'Display a list of open work assigned to me.',
  },
  [DashboardCardType.PENDING_USERS]: {
    icon: <TeamIcon />,
    heading: 'Pending User Requests',
    subheading: 'Display the number of pending user requests.',
  },
  [DashboardCardType.ASSET_COUNT]: {
    icon: <Icon icon={CategoryIcon} />,
    heading: 'Inventory Count By Inventory Type',
    subheading: 'Tracks the number of inventory per type.',
  },
  [DashboardCardType.PAST_DUE_WORK]: {
    icon: <Icon icon={WorkIcon} />,
    heading: 'Past Due Work',
    subheading: 'Displays a list of past due work.',
  },
  [DashboardCardType.RECENTLY_UPDATED]: {
    icon: <img src={activityIcon} />,
    heading: 'Recently Updated By Me',
    subheading: 'Display work, inventory and team recently updated by me.',
  },
};

const CustomizeDashboardView = ({ setView }: Props) => {
  const {
    allCards,
    setAllCards,
    lgColumn,
    smColumn,
    updateLayout,
    updatingLayout,
  } = useContext(DashboardContext);
  const optionCards: DashboardCard[] = allCards.filter(
    card => card.type !== 'EMBED',
  );

  const onChange = async cardUpdated => {
    const isEnabling = cardUpdated.order === -1;
    const targetColumnIdx = getColumnIndex(cardUpdated.size);
    const columns: ColumnsData = getColumnsData(lgColumn, smColumn);
    const newOrder = isEnabling ? columns[targetColumnIdx].length : -1;

    if (isEnabling) {
      columns[targetColumnIdx] = [...columns[targetColumnIdx], cardUpdated.id];
    } else {
      columns[targetColumnIdx] = R.without(
        [cardUpdated.id],
        columns[targetColumnIdx],
      );
    }

    try {
      updateLayout(columns);
      const updatedCards = updateAtId<DashboardCard>(
        cardUpdated.id,
        R.evolve({
          order: () => newOrder,
          contentState: () => DashboardCardContentState.LOADING,
        }),
      )(allCards);
      setAllCards(updatedCards);
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const disabled = updatingLayout || R.isEmpty(allCards);

  return (
    <div className={classes.customizeDashboardOptions}>
      <div
        className={classes.customizeDashboardOption}
        onClick={() => setView(ModalView.ADD_CUSTOM)}
      >
        <div className={classes.customizeDashboardLabel}>
          <div className={classes.optionIcon}>
            <Icon icon={CodeIcon} />
          </div>
          <div>
            <div className={classes.customizeOptionHeading}>Custom Embed</div>
            <span className={classes.customizeOptionSubheading}>
              Insert an embed link.
            </span>
          </div>
        </div>
        <div className={classes.customizeOptionRight}>
          <Icon icon={ChevronRightIcon} />
        </div>
      </div>
      {optionCards.map(card =>
        getOption(
          { ...card, ...DEFAULT_CARD_TYPE_OPTIONS[card.type] },
          onChange,
          disabled,
        ),
      )}
    </div>
  );
};

export default CustomizeDashboardView;
